import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { costsValidator } from '@k12/validators';
import { CommonModule } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatCard, MatCardActions } from '@angular/material/card';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, take, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  SchoolRosterStudent,
  SubTask,
  Task,
  TaskTypeAction
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';

interface CostCorrectionFormData {
  studentEnrollmentId: string;
  fallEnrollmentId: string;
  springEnrollmentId: string;
  schoolName: string;
  householdName: string;
  studentName: string;
  studentPid: string;
  termName: string;
  originalStandardCost: number;
  originalIndividualCost: number;
  originalFallTermCost: number;
  originalSpringTermCost: number;
  newStandardCost: number;
  newIndividualCost: number;
  newTermCostFall: number;
  newTermCostSpring: number;
  reasonForCorrection: string;
}

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-school-cost-correction-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInput,
    MatCard,
    MatButton,
    MatCardActions,
    MatFormField,
    MatLabel,
    MatHint,
    MatError,
    MatProgressSpinnerModule
  ],
  templateUrl: './school-cost-correction-form.component.html',
  styleUrl: './school-cost-correction-form.component.scss'
})
export class SchoolCostCorrectionFormComponent implements OnInit, OnDestroy {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() task: Task | null = null;
  @Input() taskTypeAction: TaskTypeAction | null = null;
  @Input() taskAction: SubTask | null = null;
  @Input() isSubmitting = false;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  protected viewMode: 'readonly' | 'edit' = 'edit';
  public form!: FormGroup; // Made public for testing
  protected isLoadingData = false;
  protected loadError: string | null = null;
  private savedFormData: CostCorrectionFormData | null = null;

  public ngOnInit(): void {
    this.assertRequiredInputs();
    this.initializeForm();
    this.lockReadOnlyFields();
    this.parseSavedFormData();
    this.loadEnrollmentData();
    this.applyReadOnlyStatus();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private assertRequiredInputs(): void {
    if (!this.task || !this.taskTypeAction || !this.taskAction) {
      throw new Error('Task, TaskTypeAction, and TaskAction are required inputs');
    }
    if (!this.taskAction.id) {
      throw new Error('TaskAction must have an id');
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group(
      {
        // Read-only display fields
        schoolName: [{ value: null, disabled: true }],
        householdName: [{ value: null, disabled: true }],
        studentName: [{ value: null, disabled: true }],
        studentPid: [{ value: null, disabled: true }],
        academicYear: [{ value: null, disabled: true }],
        termName: [{ value: null, disabled: true }],

        // Hidden reference fields
        studentEnrollmentId: [null],
        fallEnrollmentId: [null],
        springEnrollmentId: [null],

        // Original values (for comparison)
        originalStandardCost: [null],
        originalIndividualCost: [null],
        originalFallTermCost: [null],
        originalSpringTermCost: [null],

        // New editable values
        newStandardCost: [null, [Validators.required, Validators.min(0)]],
        newIndividualCost: [null, [Validators.required, Validators.min(0)]],
        newTermCostFall: [null, [Validators.required, Validators.min(0)]],
        newTermCostSpring: [null, [Validators.required, Validators.min(0)]],

        // Reason
        reasonForCorrection: [null, [Validators.required, Validators.maxLength(500)]]
      },
      {
        validators: [
          this.atLeastOneChangeValidator.bind(this),
          costsValidator({
            standardCostFieldName: 'newStandardCost',
            individualCostFieldName: 'newIndividualCost',
            termCostPrefix: 'newTermCost'
          })
        ]
      }
    );
  }

  private lockReadOnlyFields(): void {
    // Already disabled in initialization
  }

  private parseSavedFormData(): void {
    if (!this.taskAction?.formData) return;

    try {
      this.savedFormData = JSON.parse(this.taskAction.formData);
    } catch (error) {
      console.error('Failed to parse saved form data:', error);
      this.savedFormData = null;
    }
  }

  private restoreUserEdits(): void {
    if (!this.savedFormData) return;

    // Only restore user-edited fields (the "new" values and reason)
    // Don't overwrite the read-only context fields or "original" values from API
    const restoredValues: Partial<CostCorrectionFormData> = {};

    // Only restore fields that exist in saved data (defensive programming)
    if (this.savedFormData.newStandardCost !== undefined && this.savedFormData.newStandardCost !== null) {
      restoredValues.newStandardCost = this.savedFormData.newStandardCost;
    }
    if (this.savedFormData.newIndividualCost !== undefined && this.savedFormData.newIndividualCost !== null) {
      restoredValues.newIndividualCost = this.savedFormData.newIndividualCost;
    }
    if (this.savedFormData.newTermCostFall !== undefined && this.savedFormData.newTermCostFall !== null) {
      restoredValues.newTermCostFall = this.savedFormData.newTermCostFall;
    }
    if (this.savedFormData.newTermCostSpring !== undefined && this.savedFormData.newTermCostSpring !== null) {
      restoredValues.newTermCostSpring = this.savedFormData.newTermCostSpring;
    }
    if (this.savedFormData.reasonForCorrection !== undefined && this.savedFormData.reasonForCorrection !== null) {
      restoredValues.reasonForCorrection = this.savedFormData.reasonForCorrection;
    }

    // Apply restored values - this happens AFTER API load, so it overwrites the pre-populated values
    this.form.patchValue(restoredValues, { emitEvent: false });

    console.log('Restored user edits from saved formData:', restoredValues);
  }

  private loadEnrollmentData(): void {
    const studentEnrollmentId = this.getStudentEnrollmentIdFromTask();
    if (!studentEnrollmentId) {
      this.loadError = 'No student enrollment ID found in task data';
      this.cdr.markForCheck();
      return;
    }

    this.isLoadingData = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.schoolsService
      .getStudentRoster(studentEnrollmentId)
      .pipe(
        take(1),
        tap((response) => {
          if (!response?.entity) {
            throw new Error('No enrollment data returned from API');
          }

          const primaryRoster = response.entity;

          // Construct household name from parent fields
          const householdName =
            [primaryRoster.parentFirstName, primaryRoster.parentLastName].filter(Boolean).join(' ') || '..';

          // Populate ONLY read-only context fields (all disabled fields)
          // These are informational only and safe to update from API
          this.form.patchValue(
            {
              studentEnrollmentId: primaryRoster.studentEnrollmentId,
              schoolName: primaryRoster.schoolName || '..',
              householdName: householdName,
              studentName: `${primaryRoster.studentFirstName} ${primaryRoster.studentLastName}`,
              studentPid: primaryRoster.pidNumber || '..',
              academicYear: primaryRoster.academicYearName || '..',
              termName: primaryRoster.termName || '..'
            },
            { emitEvent: false }
          );

          // Now load sibling enrollment to get all cost data
          return this.loadSiblingEnrollmentAndCosts(primaryRoster);
        }),
        catchError((error) => {
          console.error('Error loading enrollment data:', error);
          this.loadError = 'Failed to load enrollment data. Please try again or contact support.';
          this.isLoadingData = false;
          this.cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  private loadSiblingEnrollmentAndCosts(primaryRoster: SchoolRosterStudent): void {
    if (primaryRoster.crossTermSummary?.length !== 2) {
      this.handleMissingSiblingEnrollment(primaryRoster);
      return;
    }
    // load other enrollment.
    const otherEntry = primaryRoster.crossTermSummary
      .find((s) => s.studentEnrollmentId !== primaryRoster.studentEnrollmentId);

    if (!otherEntry?.studentEnrollmentId) {
      this.handleMissingSiblingEnrollment(primaryRoster);
      return;
    }

    this.schoolsService
      .getStudentRoster(otherEntry.studentEnrollmentId)
      .pipe(
        take(1),
        tap((response) => {
          if (!response?.entity) {
            throw new Error('No enrollment data returned from API');
          }

          const secondaryRoster = response.entity;

          this.handleEnrollmentCosts(primaryRoster, secondaryRoster);
        }),
        catchError((error) => {
          console.error('Error loading enrollment data:', error);
          this.loadError = 'Failed to load enrollment data. Please try again or contact support.';
          this.isLoadingData = false;
          this.cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();

    // Query for sibling enrollment
    // In a real implementation, we'd have an API to query by studentId + academicYearId
    // For now, we'll use a workaround by checking if we can infer the sibling from context

    // Placeholder logic - in real implementation, call API to get both enrollments
  }

  private handleEnrollmentCosts(primaryRoster: SchoolRosterStudent, siblingRoster: SchoolRosterStudent | null): void {
    // Determine which is Fall and which is Spring
    const primaryIsFall = primaryRoster.termName?.toLowerCase().includes('fall');

    const fallRoster = primaryIsFall ? primaryRoster : siblingRoster;
    const springRoster = primaryIsFall ? siblingRoster : primaryRoster;

    // Extract costs
    const standardCost = primaryRoster.academicYearStandardCostTotal ?? 0;
    const individualCost = primaryRoster.academicYearIndividualCostTotal ?? 0;
    const fallTermCost = fallRoster?.termCost ?? 0;
    const springTermCost = springRoster?.termCost ?? 0;

    // Phase 1: Populate form with API data
    // - Hidden reference IDs for submission
    // - "Original" cost values (for comparison and validation)
    // - Pre-populate "new" cost values with current values (starting point for NEW forms)
    // NOTE: For forms with saved data, the "new" values will be overwritten in Phase 2
    this.form.patchValue(
      {
        fallEnrollmentId: fallRoster?.studentEnrollmentId || '',
        springEnrollmentId: springRoster?.studentEnrollmentId || '',
        originalStandardCost: standardCost,
        originalIndividualCost: individualCost,
        originalFallTermCost: fallTermCost,
        originalSpringTermCost: springTermCost,
        // Pre-populate new values with current values (for new forms)
        newStandardCost: standardCost,
        newIndividualCost: individualCost,
        newTermCostFall: fallTermCost,
        newTermCostSpring: springTermCost
      },
      { emitEvent: false }
    );

    // Phase 2: Restore any saved user edits
    // If savedFormData exists, this will OVERWRITE the pre-populated "new" values above
    // This ensures user's in-progress work is NEVER lost, even after API refresh
    this.restoreUserEdits();

    this.isLoadingData = false;
    this.cdr.markForCheck();
  }

  private handleMissingSiblingEnrollment(primaryRoster: SchoolRosterStudent): void {
    // Single enrollment only - still allow form to work
    const standardCost = primaryRoster.academicYearStandardCostTotal ?? 0;
    const individualCost = primaryRoster.academicYearIndividualCostTotal ?? 0;
    const termCost = primaryRoster.termCost ?? 0;

    const isFall = primaryRoster.termName?.toLowerCase().includes('fall');

    // Phase 1: Populate form with API data (see comments in handleEnrollmentCosts)
    this.form.patchValue(
      {
        fallEnrollmentId: isFall ? primaryRoster.studentEnrollmentId : '',
        springEnrollmentId: isFall ? '' : primaryRoster.studentEnrollmentId,
        originalStandardCost: standardCost,
        originalIndividualCost: individualCost,
        originalFallTermCost: isFall ? termCost : 0,
        originalSpringTermCost: isFall ? 0 : termCost,
        newStandardCost: standardCost,
        newIndividualCost: individualCost,
        newTermCostFall: isFall ? termCost : 0,
        newTermCostSpring: isFall ? 0 : termCost
      },
      { emitEvent: false }
    );

    // Phase 2: Restore any saved user edits (protects against API overwrite)
    this.restoreUserEdits();

    this.loadError = 'Note: Only one term enrollment found. Some cost fields may be zero.';
    this.isLoadingData = false;
    this.cdr.markForCheck();
  }

  private getStudentEnrollmentIdFromTask(): string | null {
    // Check taskAction formData
    if (this.taskAction?.formData) {
      try {
        const data = JSON.parse(this.taskAction.formData);
        if (data.studentEnrollmentId) return data.studentEnrollmentId;
      } catch {
        /* empty */
      }
    }

    return null;
  }

  private atLeastOneChangeValidator(group: AbstractControl): ValidationErrors | null {
    const formGroup = group as FormGroup;

    const standardChanged = formGroup.get('newStandardCost')?.value !== formGroup.get('originalStandardCost')?.value;
    const individualChanged =
      formGroup.get('newIndividualCost')?.value !== formGroup.get('originalIndividualCost')?.value;
    const fallChanged = formGroup.get('newTermCostFall')?.value !== formGroup.get('originalFallTermCost')?.value;
    const springChanged = formGroup.get('newTermCostSpring')?.value !== formGroup.get('originalSpringTermCost')?.value;

    const hasAnyChange = standardChanged || individualChanged || fallChanged || springChanged;

    return hasAnyChange ? null : { noChanges: true };
  }

  private applyReadOnlyStatus(): void {
    const statusId = this.taskAction?.taskStatusId;
    if (statusId !== 1 && statusId !== 4) {
      this.form.disable();
      this.viewMode = 'readonly';
    }
  }

  public onSubmit(): void {
    if (this.form.invalid || this.isSubmitting) return;

    const formValue = this.form.getRawValue();

    this.submitFormData.emit(JSON.stringify(formValue));
  }
}
